import moment from "moment";
import 'moment-timezone';
import {getLocalization} from "./localization";
import {debounce} from "throttle-debounce";
import {STRATEGIC_PARTNER} from "../constants/strategicPartners";
import anselConfig from "../constants/anselConfig";
import symetraConfig from "../constants/symetraConfig";
import nationwideConfig from "../constants/nationwideConfig";
import aigConfig from "../constants/aigConfig";
import renaissanceConfig from "../constants/renaissanceConfig";
import {RegisterActionRequest} from "../services/types";
import {sendRegisterAction, upsertUserProgress} from "../services/services";

export const VERIFY_ITS_YOU_ROUTE = "/verify";
export const SELECT_PLAN_ROUTE = "/plan";
export const INCOME_ROUTE = "/income";
export const HEALTH_PLAN_DETAILS_ROUTE = "/healthPlanDetails";
export const TELL_US_ABOUT_YOU_ROUTE = "/tellUsAboutYou";
export const LEARN_ABOUT_COVERAGE_ROUTE = "/learnAboutCoverage";
export const CONDITIONS_ROUTE = "/conditions";
export const PLAN_PAYS_ROUTE = "/howOurPlanPays"
export const CLAIMS_ROUTE = "/claims";
export const WOULD_YOU_MIND_ROUTE = "/wouldYouMind";
export const PLUS_ROUTE = "/plus";
export const ADDITIONAL_INFO_ROUTE = "/additionalInfo";
export const SUMMARY_ROUTE = "/summary";
export const SUMMARY_RETURNING_ROUTE = "/summaryReturning";
export const WHY_ROUTE = `/why${STRATEGIC_PARTNER.LABEL.split(" ")}`;
export const FRUSTRATIONS_ROUTE = "/frustrations";
export const REVIEW_ROUTE = "/review";
export const EXPIRED_ROUTE = "/expired";
export const REQUEST_SENT_ROUTE = "/requestSent";
export const SUCCESS_ROUTE = "/success";
export const NOT_IN_OPEN_ENROLLMENT_ROUTE = "/noOE";
export const NO_CONTRACT_TO_ENROL = "/NoContractToEnroll";
export const RESET_PASSWORD = "/resetPassword";
export const FORGOT_PASSWORD = "/forgotPassword";
export const PASSWORD_RESET_REQUEST_SENT = "/passwordResetRequestSent";
export const ELIGIBILITY_LOSS = "/eligibilityIssue";
export const COVERAGE_DECLINED_PATH = "/thankYou";
export const LINK_EXPIRED_ROUTE = "/linkExpired";

export const AFFILIATE_LINK_EXPIRED_ERROR = "AFFILIATE_LINK_EXPIRED";

export const uiFlowStandalone = "standalone";
export const uiFlowBenAdmin = "benefitsadmin";
export const uiFlowUnsubscribe = "unsubscribe";
export const uiFlowResetToken = "resetToken";

export const isAssociationContract = (contractType) => contractType === 'Association';

export const distributionChannelIndividual = "Individual";
export const isDistributionChannelIndividual = (distributionChannel) => distributionChannel === distributionChannelIndividual;

const individualStepsCount = 2;

export const getEnrollmentTimezone = (initialData) => {
    const timezone = initialData?.employer?.timezone;
    if (!timezone && isDistributionChannelIndividual(initialData?.distributionChannel)) {
        return moment.tz.guess();
    }
    return timezone;
};

export const EDUCATIONAL_AUTO_ADVANCE_SECONDS = 20;

export const getBenAdminGreetingName = (benAdmin, distributionChannel) => {
    if (isDistributionChannelIndividual(distributionChannel) && !!benAdmin.lastName) {
        return `${benAdmin.name} ${benAdmin.lastName}`;
    }
    return benAdmin.name;
};
export const displayDateFormat = "MM/DD/YYYY";
export const serverDateFormat = "YYYY-MM-DD";

export const domesticPartnershipText = "Domestic Partner";
export const spouseText = "Spouse";
export const civilUnionText = "Civil Union";

export const getSpouseEligibilityString = (domesticPartnerEligible, civilUnionEligible, strings) => {
    if (domesticPartnerEligible && civilUnionEligible) {
        return strings.includesDpAndCu;
    } else if (domesticPartnerEligible) {
        return strings.includesOnlyDp;
    } else if (civilUnionEligible) {
        return strings.includesOnlyCu;
    }
    return null;
};

export const languages = {
    ENGLISH: "en",
    SPANISH: "es",
};

export const BREAKPOINTS = {
    MOBILE: 0,
    TABLET: 768,
    DESKTOP: 992,
    LARGE_SCREEN: 1200,
    WIDE_SCREEN: 1920,
};

export const savePoints = {
    [TELL_US_ABOUT_YOU_ROUTE]: {step: 1, redirectTo: FRUSTRATIONS_ROUTE,},
    [HEALTH_PLAN_DETAILS_ROUTE]: {step: 2, redirectTo: INCOME_ROUTE,},
    [INCOME_ROUTE]: {step: 3, redirectTo: SELECT_PLAN_ROUTE,},
    [SELECT_PLAN_ROUTE]: {step: 4, redirectTo: SUMMARY_ROUTE,},
    [ADDITIONAL_INFO_ROUTE]: {step: 5, redirectTo: REVIEW_ROUTE,},
};

export const handleSaveUserProgress = async (token, userObj, handleStart, handleSuccess, handleError) => {
    if (!userObj.enrolleeSfId || !token) {
        return
    }

    try {
        !!handleStart && handleStart();
        const payload = {
            token: token,
            userProgress: {
                enrolleeSfId: userObj.enrolleeSfId,
                data: {
                    user: userObj,
                },
            }
        };
        await upsertUserProgress(payload);
        !!handleSuccess && handleSuccess();
    } catch (e) {
        console.error(e);
        !!handleError && handleError(e);
    }
}

export const getStepProps = (localizedStrings, activeStepIdx, uiFlow, distributionChannel) => {
    const strings = localizedStrings.steps;
    const stepProps = [
        {
            active: false,
            completed: false,
            disabled: true,
            label: strings.step1,
        }, {
            active: false,
            completed: false,
            disabled: true,
            label: strings.step2,
        }, {
            active: false,
            completed: false,
            disabled: true,
            label: strings.step3,
        }
    ];
    if (uiFlow === uiFlowStandalone) {
        stepProps.push({
            active: false,
            completed: false,
            disabled: true,
            label: strings.step4,
        });
    }

    if (isDistributionChannelIndividual(distributionChannel)) {
        stepProps.length = individualStepsCount;
    }

    const activeStep = Math.min(activeStepIdx, stepProps.length - 1);
    for (let i = 0; i < activeStep; i++) {
        stepProps[i].disabled = false;
        stepProps[i].completed = true;
    }
    stepProps[activeStep].active = true;
    stepProps[activeStep].disabled = false;
    return stepProps;
}

export const checkIsVerified = (userReducerData, benAdminReducerData, uiFlow, props, distributionChannel) => {
    if (!userReducerData || !uiFlow) {
        return
    }
    const isBenAdmin = uiFlow === uiFlowBenAdmin;
    const isIndividual = isDistributionChannelIndividual(distributionChannel);
    if (isBenAdmin && !userReducerData.validEmployerPasscode) {
        props.history.replace("/");
    } else if (isBenAdmin && !isIndividual && benAdminReducerData.name.length === 0) {
        props.history.replace(VERIFY_ITS_YOU_ROUTE);
    } else if (!isBenAdmin && !userReducerData.validSsnVerification) {
        props.history.replace(VERIFY_ITS_YOU_ROUTE);
    }
}

export const convertDisplayDateFormatToServer = (dateStr) => {
    const [month, day, yyyy] = dateStr.split("/");
    const mm = (`0${+month}`).slice(-2);
    const dd = (`0${+day}`).slice(-2);
    return `${yyyy}-${mm}-${dd}`;
}

export const formatPartialDate = (partialDateStr, prevValue) => {
    let response = partialDateStr;
    const userIsAddingCharacters = !prevValue || partialDateStr.length > prevValue.length;
    if (userIsAddingCharacters) {
        if (partialDateStr.length === 2) {
            response = partialDateStr + "/";
        } else if (partialDateStr.length === 5) {
            response = partialDateStr + "/";
        }
    } else {
        if (partialDateStr.length === 2) {
            response = partialDateStr.substring(0, 1)
        } else if (partialDateStr.length === 5) {
            response = partialDateStr.substring(0, 4)
        }
    }

    return response.substring(0, 10);
}

export const parseDate = (value) => {
    return moment(value, displayDateFormat, true);
}

export const findEmployerContribution = (optionValue, options, obj) => {
    if (optionValue === null || typeof optionValue === "undefined") {
        return 0.0;
    }
    let selected = '';
    options.forEach((o) => {
        if (o.value === optionValue) {
            selected = o.value;
        }
    });
    switch (selected) {
        case 'child':
            return obj.employeeChild;
        case 'family':
            return obj.family;
        case 'spouse':
            return obj.employeeSpouse;
        case 'employee':
            return obj.employeeOnly
        default:
            return 0.0;
    }
}

export const getPrevNextStoryFromName = (nameOfCurrentStory) => {
    let response = {};
    const baseRoute = PLAN_PAYS_ROUTE
    const rayRoute = `${baseRoute}/1`
    const amberRoute = `${baseRoute}/2`
    const serenaRoute = `${baseRoute}/3`
    const marcusRoute = `${baseRoute}/4`
    switch (nameOfCurrentStory) {
        case "Ray":
            response.prev = marcusRoute;
            response.next = amberRoute;
            break;
        case "Amber":
            response.prev = rayRoute;
            response.next = serenaRoute;
            break;
        case "Serena":
            response.prev = amberRoute;
            response.next = marcusRoute;
            break;
        case "Marcus":
            response.prev = serenaRoute;
            response.next = rayRoute;
            break;
        default:
            response = {prev: '', next: ''};
    }

    return response;
}

export const formatMoneyAmount = (value) => {
    value = truncTwoDec(value);
    return value.toLocaleString('en-US');
}

export const truncTwoDec = (num) => {
    if (typeof num === "string") {
        return num
    }

    const fixed = num.toFixed(2);
    const parts = fixed.split(".")
    if (parts.length > 1 && parts[1] === "00") {
        return Math.floor(num * 100) / 100;
    }
    return fixed;
};


export const getTranslatedCoverageText = (value, localization) => {
    const strings = localization.summary;
    if (value === 'employee') {
        return strings.coverMySelf;
    } else if (value === 'spouse') {
        return strings.coverSpouse;
    } else if (value === 'child') {
        return strings.coverChild;
    } else if (value === 'family') {
        return strings.coverFamily;
    }
}

export const getTranslatedGenderText = (text, localization) => {
    const strings = localization.additionalInfo;
    const localizationEng = getLocalization(languages.ENGLISH);
    const engStrings = localizationEng.additionalInfo;
    if (text === engStrings.female) {
        text = strings.female;
    } else if (text === engStrings.male) {
        text = strings.male;
    }
    return text;
}

export const getTranslatedStudentStatusText = (text, localization) => {
    const strings = localization.additionalInfo;
    const localizationEng = getLocalization(languages.ENGLISH);
    const engStrings = localizationEng.additionalInfo;
    if (text === engStrings.studentStatusPartTime) {
        text = strings.studentStatusPartTime;
    } else if (text === engStrings.studentStatusFullTime) {
        text = strings.studentStatusFullTime;
    } else if (text === engStrings.studentStatusNotAStudent) {
        text = strings.studentStatusNotAStudent;
    }
    return text;
}

export const getTranslatedPhoneTypeText = (text, localization) => {
    const strings = localization.additionalInfo;
    const localizationEng = getLocalization(languages.ENGLISH);
    const engStrings = localizationEng.additionalInfo;
    if (text === engStrings.mobile) {
        text = strings.mobile;
    } else if (text === engStrings.home) {
        text = strings.home;
    } else if (text === engStrings.standard) {
        text = strings.standard;
    }
    return text;
}

export const getTranslatedRelationshipText = (text, localization) => {
    const strings = localization.additionalInfo;
    const localizationEng = getLocalization(languages.ENGLISH);
    const engStrings = localizationEng.additionalInfo;
    if (text === engStrings.child) {
        text = strings.child;
    } else if (text.toLowerCase() === engStrings.domesticPartner.toLowerCase()) {
        text = strings.domesticPartner;
    } else if (text.toLowerCase() === engStrings.civilUnion.toLowerCase()) {
        text = strings.civilUnion;
    } else if (text === engStrings.spouse) {
        text = strings.spouse;
    }
    return text;
}

export const resolveErrorMessage = (e, defaultError) => e.response?.data?.errorMessage || defaultError;

const registerAction = async (token, action, data) => {
    try {
        const request = new RegisterActionRequest({
            token,
            actionType: action,
            data
        });
        await sendRegisterAction(request);
    } catch (e) {
        console.log(e);
    }
};

const doRegisterAction = debounce(1000, registerAction);

export const registerNavigation = (token) => {
    doRegisterAction(token, 'NAVIGATE', {route: window.location.pathname});
}

export const registerSkipRecommendation = (token) => {
    registerAction(token, 'SKIP_REC'); // not throttled on purpose
}

export const registerEmailRecommendation = (token) => {
    doRegisterAction(token, 'EMAIL');
}

export const registerPrintRecommendation = (token) => {
    doRegisterAction(token, 'PRINT');
}

export const firstCharUpper = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isEmailValid(email) {
    let emailWithoutSpaces = email
    if (email) {
        emailWithoutSpaces = email.replace(/ /g, '');
    }
    if (email !== emailWithoutSpaces) {
        return false;
    }
    return isValidEmail(emailWithoutSpaces);
}

const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\])|(([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = email => {
    if (!email || typeof email !== 'string' || /^\s|\s$/.test(email)) {
        return false;
    }
    return emailPattern.test(email);
};

export function getArrayByLanguage(array, lang) {
    return array.filter(i => i.language === lang)
}

export function getNumberOfDeductions(payrollFrecuencySelected) {

    let payrollFrecuencySelectedMap = new Map();
    payrollFrecuencySelectedMap.set("Bi-weekly (26 deductions)", 26)
    payrollFrecuencySelectedMap.set("Bi-weekly (24 deductions)", 24)
    payrollFrecuencySelectedMap.set("Semi-monthly (24 deductions)", 24)
    payrollFrecuencySelectedMap.set("Monthly", 12)
    payrollFrecuencySelectedMap.set("Weekly (52 deductions)", 52)

    return payrollFrecuencySelectedMap.get(payrollFrecuencySelected)
}

export function stringTemplate(string, vars = {}) {
    const regex = /{{(\w+)}}/g
    return string.replaceAll(regex, (_, key) => vars[key] || key);
}


export function checkIsOpenEnrollment(start, end, tzStr) {
    let isOpenEnrollment = false;
    const now = moment().tz(tzStr);
    if (moment(now.format("YYYY-MM-DD")).isBetween(start, end, 'days', '[]')) {
        isOpenEnrollment = true;
    }
    return isOpenEnrollment;
}

export function currentYear() {
    return new Date().getFullYear()
}

export const buildFullName = ({firstName, middleName, lastName, suffix, relation}) => {
    let fullName = []
    if (firstName) {
        fullName.push(firstName)
    }
    if (middleName) {
        fullName.push(middleName)
    }
    if (lastName) {
        fullName.push(lastName)
    }
    if (suffix) {
        fullName.push(suffix)
    }
    if (relation) {
        fullName.push(`(${relation})`)
    }

    return fullName.join(" ")
}

export const getTierName = (tiers) => {
    const selectedTier = Object.keys(tiers).find(key => tiers[key] === true);
    return selectedTier ? firstCharUpper(selectedTier) : ''
}

export const hasTierSelected = (tiers) => {
    return tiers && Object.keys(tiers).find(key => tiers[key] === true);
}

export function isAnsel() {
    return STRATEGIC_PARTNER.NAME === anselConfig.NAME
}

export function isSymetra() {
    return STRATEGIC_PARTNER.NAME === symetraConfig.NAME
}

export function isNationwide() {
    return STRATEGIC_PARTNER.NAME === nationwideConfig.NAME
}

export function isAIG() {
    return STRATEGIC_PARTNER.NAME === aigConfig.NAME
}

export function isRenaissance() {
    return STRATEGIC_PARTNER.NAME === renaissanceConfig.NAME
}
